export const AUTH_STORAGE_KEY = 'brutos-auth-session';
export const AUTH_SESSION_INVALID_EVENT = 'brutos:auth-session-invalid';
export const AUTH_SESSION_CHANGING_EVENT = 'brutos:auth-session-changing';
export const AUTH_SESSION_INVALIDATION_STORAGE_KEY = 'brutos-auth-session-invalidation-v1';
let pageAuthToken;
let pageAuthSession;

export const USER_ROLES = {
  ADMINISTRADOR: 'administrador',
  COLABORADOR: 'colaborador',
  GARCOM: 'garcom',
  GERENTE: 'gerente',
  PROGRAMADOR: 'programador',
  CAIXA: 'caixa',
};

export const parseStoredSessionValue = (raw) => {
  try {
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token || !parsed?.user) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const getStoredSession = () => {
  if (typeof window === 'undefined') return null;
  try {
    const storedSession = parseStoredSessionValue(window.localStorage.getItem(AUTH_STORAGE_KEY));
    if (pageAuthToken === undefined) {
      pageAuthToken = String(storedSession?.token || '').trim();
      pageAuthSession = storedSession;
    }
    return storedSession;
  } catch {
    if (pageAuthToken === undefined) {
      pageAuthToken = '';
      pageAuthSession = null;
    }
    return null;
  }
};

export const setStoredSession = (session) => {
  if (typeof window === 'undefined') return;
  const storedSession = session?.token && session?.user
    ? { ...session, browserSessionId: buildInvalidationEventId() }
    : session;
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(storedSession));
  pageAuthToken = String(storedSession?.token || '').trim();
  pageAuthSession = storedSession || null;
  return storedSession;
};

export const clearStoredSession = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
  pageAuthToken = '';
  pageAuthSession = null;
};

export const isStoredSessionCurrent = (expectedSession) => {
  if (typeof window === 'undefined') return false;
  try {
    const storedSession = parseStoredSessionValue(
      window.localStorage.getItem(AUTH_STORAGE_KEY)
    );
    return String(storedSession?.token || '').trim() === String(expectedSession?.token || '').trim()
      && getAuthSessionTenantKey(storedSession) === getAuthSessionTenantKey(expectedSession);
  } catch {
    return false;
  }
};

export const isStoredSessionTokenCurrent = (expectedToken) => {
  if (typeof window === 'undefined') return false;
  try {
    const normalizedExpectedToken = String(expectedToken || '').trim();
    const storedToken = String(parseStoredSessionValue(
      window.localStorage.getItem(AUTH_STORAGE_KEY)
    )?.token || '').trim();
    return Boolean(normalizedExpectedToken) && storedToken === normalizedExpectedToken;
  } catch {
    return false;
  }
};

export const getAuthToken = () => {
  if (pageAuthToken === undefined) {
    pageAuthToken = String(getStoredSession()?.token || '').trim();
  }
  return pageAuthToken;
};

export const pinPageAuthSession = (session) => {
  pageAuthToken = String(session?.token || '').trim();
  pageAuthSession = session || null;
};

export const invalidatePageAuthSession = () => {
  pageAuthToken = '';
  pageAuthSession = null;
};

export const getPageAuthSession = () => pageAuthSession || null;

export const getAuthSessionTenantKey = (session) => (
  String(session?.user?.companyId || '').trim()
);

const decodeJwtPayload = (token) => {
  try {
    if (typeof window === 'undefined' || typeof window.atob !== 'function') return null;
    const encoded = String(token || '').split('.')[1] || '';
    if (!encoded) return null;
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
};

const normalizeAuthTokenVersion = (value) => {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : 1;
};

export const getAuthSessionMarker = (session) => {
  const token = String(session?.token || '').trim();
  const payload = decodeJwtPayload(token);
  if (!token || !payload) return '';

  return JSON.stringify([
    String(session?.browserSessionId || ''),
    String(session?.user?.id ?? payload.id ?? ''),
    getAuthSessionTenantKey(session) || String(payload.companyId || ''),
    normalizeAuthTokenVersion(payload.authTokenVersion),
    Number(payload.iat || 0),
    Number(payload.exp || 0),
  ]);
};

const buildInvalidationEventId = () => {
  try {
    if (
      typeof window !== 'undefined'
      && typeof window.crypto?.randomUUID === 'function'
    ) {
      return window.crypto.randomUUID();
    }
  } catch {
    // Usa fallback sem dados sensíveis.
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const parseAuthSessionInvalidation = (raw) => {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed?.eventId || !parsed?.scope || !parsed?.reason) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const buildAuthSessionInvalidation = (session, reason) => {
  const marker = getAuthSessionMarker(session);
  if (!marker) return null;
  return {
    eventId: buildInvalidationEventId(),
    scope: 'session',
    marker,
    reason: String(reason || 'session_invalid'),
    at: Date.now(),
  };
};

export const buildAuthUserVersionInvalidation = (identity, reason = 'password_reset') => {
  const userId = String(identity?.userId ?? '').trim();
  const companyId = String(identity?.companyId ?? '').trim();
  const invalidBeforeVersion = normalizeAuthTokenVersion(identity?.authTokenVersion);
  if (!userId || !companyId || invalidBeforeVersion <= 1) return null;

  return {
    eventId: buildInvalidationEventId(),
    scope: 'user-version',
    userId,
    companyId,
    invalidBeforeVersion,
    reason: String(reason || 'password_reset'),
    at: Date.now(),
  };
};

export const authSessionMatchesInvalidation = (session, invalidation) => {
  const record = parseAuthSessionInvalidation(invalidation);
  if (!session?.token || !record) return false;

  if (record.scope === 'session') {
    return Boolean(record.marker) && getAuthSessionMarker(session) === record.marker;
  }

  if (record.scope === 'user-version') {
    const payload = decodeJwtPayload(session.token);
    return String(session?.user?.id ?? payload?.id ?? '') === String(record.userId)
      && (getAuthSessionTenantKey(session) || String(payload?.companyId || '')) === String(record.companyId)
      && normalizeAuthTokenVersion(payload?.authTokenVersion) < Number(record.invalidBeforeVersion);
  }

  return false;
};

export const publishAuthSessionInvalidation = (windowRef, invalidation) => {
  const record = parseAuthSessionInvalidation(invalidation);
  if (!record) return false;
  try {
    const storage = windowRef?.localStorage;
    if (!storage) return false;
    storage.setItem(
      AUTH_SESSION_INVALIDATION_STORAGE_KEY,
      JSON.stringify(record)
    );
    return true;
  } catch {
    return false;
  }
};

export const getStoredAuthSessionInvalidation = (windowRef) => {
  try {
    return parseAuthSessionInvalidation(
      windowRef?.localStorage?.getItem(AUTH_SESSION_INVALIDATION_STORAGE_KEY)
    );
  } catch {
    return null;
  }
};

const EXTERNAL_AUTH_RELOAD_EXEMPT_PATHS = new Set([
  '/mesa',
  '/kds/acesso',
  '/garcom/acesso',
  '/garcom/operacao',
  '/recuperar-senha',
  '/redefinir-senha',
]);

export const shouldReloadForExternalAuthChange = (pathname, options = {}) => {
  const normalizedPathname = String(pathname || '/').replace(/\/+$/, '') || '/';
  if (EXTERNAL_AUTH_RELOAD_EXEMPT_PATHS.has(normalizedPathname)) return false;
  if (normalizedPathname === '/kds/monitor' && options.hasKdsDeviceSession) return false;
  return true;
};

export const shouldIgnorePrimaryAuthOnBootstrap = (pathname, options = {}) => (
  !shouldReloadForExternalAuthChange(pathname, options)
);

const AUTH_BOOTSTRAP_IGNORED_LOGIN_REASONS = new Set([
  'password_reset',
  'payment_pending',
  'session_invalid',
]);

export const shouldIgnoreStoredSessionOnBootstrap = (pathname, search = '') => {
  if (String(pathname || '') !== '/login') return false;
  const reason = new URLSearchParams(String(search || '')).get('reason');
  return AUTH_BOOTSTRAP_IGNORED_LOGIN_REASONS.has(String(reason || '').trim());
};

export const shouldInvalidateForExternalAuthChange = (currentSession, nextRawValue) => {
  const nextSession = parseStoredSessionValue(nextRawValue);
  const currentToken = String(currentSession?.token || '').trim();
  const nextToken = String(nextSession?.token || '').trim();

  if (!currentToken && !nextToken) return false;
  return currentToken !== nextToken
    || getAuthSessionTenantKey(currentSession) !== getAuthSessionTenantKey(nextSession);
};

export const notifyInvalidSession = (reason = 'session_invalid') => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_INVALID_EVENT, {
    detail: { reason: String(reason || 'session_invalid') },
  }));
};

export const normalizeRole = (value) => String(value || '').trim().toLowerCase();

export const isProgrammer = (user) => normalizeRole(user?.role) === USER_ROLES.PROGRAMADOR;

export const isAdmin = (user) => normalizeRole(user?.role) === USER_ROLES.ADMINISTRADOR;

export const isAdminLike = (user) => isAdmin(user) || isProgrammer(user);

export const canAccessIfoodCatalog = (user) => isAdminLike(user);

export const canAccessFinance = (user) => isAdminLike(user);

export const canManageInputs = (user) => [USER_ROLES.PROGRAMADOR, USER_ROLES.ADMINISTRADOR, USER_ROLES.GERENTE].includes(normalizeRole(user?.role));

export const canAccessWaiter = (user) => [
  USER_ROLES.PROGRAMADOR,
  USER_ROLES.ADMINISTRADOR,
  USER_ROLES.GERENTE,
  USER_ROLES.COLABORADOR,
  USER_ROLES.GARCOM,
].includes(normalizeRole(user?.role));

export const canAccessBackoffice = (user) => [
  USER_ROLES.PROGRAMADOR,
  USER_ROLES.ADMINISTRADOR,
  USER_ROLES.GERENTE,
  USER_ROLES.COLABORADOR,
].includes(normalizeRole(user?.role));

export const canAccessSalon = (user) => [
  USER_ROLES.PROGRAMADOR,
  USER_ROLES.ADMINISTRADOR,
  USER_ROLES.GERENTE,
  USER_ROLES.COLABORADOR,
  USER_ROLES.CAIXA,
].includes(normalizeRole(user?.role));

export const canAccessKdsMonitor = (user) => [
  USER_ROLES.PROGRAMADOR,
  USER_ROLES.ADMINISTRADOR,
  USER_ROLES.GERENTE,
  USER_ROLES.COLABORADOR,
  USER_ROLES.GARCOM,
].includes(normalizeRole(user?.role));

export const canManageSalonTables = (user) => [
  USER_ROLES.PROGRAMADOR,
  USER_ROLES.ADMINISTRADOR,
  USER_ROLES.GERENTE,
  USER_ROLES.COLABORADOR,
].includes(normalizeRole(user?.role));

export const canManageSalonBranding = (user) => [
  USER_ROLES.PROGRAMADOR,
  USER_ROLES.ADMINISTRADOR,
].includes(normalizeRole(user?.role));

export const canCloseSalonSession = canAccessSalon;

export const canCancelSentSalonItem = (user) => [
  USER_ROLES.PROGRAMADOR,
  USER_ROLES.ADMINISTRADOR,
  USER_ROLES.GERENTE,
].includes(normalizeRole(user?.role));

export const canAccessProgrammerConfig = (user) => isProgrammer(user);

export const canAccessIntegrationConfig = (user) => isProgrammer(user);

export const canManageOtherUsers = (user) => isAdminLike(user);
